// /src/pages/gro/QuestionnaireFormPage.tsx
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Save } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateQuestionnaire,
  useQuestionnaireDetail,
  useQuestionnaireVersionDetail,
  useQuestionnaireVersions,
  useUpdateQuestionnaire,
} from "@/hooks/useQuestionnaires";
import type {
  QuestionnaireCreateIn,
  QuestionnaireUpdateIn,
} from "@/types/gro";
import type { QuestionnaireScope } from "@/services/questionnaires";

const DEFAULT_CREATE: QuestionnaireCreateIn = {
  code: "",
  name: "",
  description: "",
  initial_version_label: "v1",
  instructions: "",
  scoring_method: "likert",
};

function normalizeQuestionnaireCode(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, "").slice(0, 3).toUpperCase())
    .filter(Boolean)
    .join("_")
    .slice(0, 60);
}

type QuestionnaireFormPageProps = {
  scope?: QuestionnaireScope;
  basePath?: string;
};

export default function QuestionnaireFormPage({
  scope = "client",
  basePath = "/gro/questionnaires",
}: QuestionnaireFormPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const detailQ = useQuestionnaireDetail(id, { scope });
  const versionsQ = useQuestionnaireVersions(id);

  const currentEditVersionId = useMemo(() => {
    if (!isEditing) return null;
    return versionsQ.data?.[0]?.id ?? null;
  }, [isEditing, versionsQ.data]);

  const editVersionQ = useQuestionnaireVersionDetail(
    isEditing && currentEditVersionId ? currentEditVersionId : undefined,
  );

  const createMut = useCreateQuestionnaire();
  const updateMut = useUpdateQuestionnaire();

  const [code, setCode] = useState(DEFAULT_CREATE.code);
  const [name, setName] = useState(DEFAULT_CREATE.name);
  const [description, setDescription] = useState(DEFAULT_CREATE.description ?? "");
  const [initialVersionLabel, setInitialVersionLabel] = useState(
    DEFAULT_CREATE.initial_version_label ?? "v1",
  );
  const [instructions, setInstructions] = useState(
    DEFAULT_CREATE.instructions ?? "",
  );
  const [isActive, setIsActive] = useState(true);
  const [codeTouched, setCodeTouched] = useState(false);

  useEffect(() => {
    if (!isEditing || !detailQ.data) return;
    setCode(detailQ.data.code);
    setName(detailQ.data.name);
    setDescription(detailQ.data.description ?? "");
    setIsActive(detailQ.data.is_active);
    setCodeTouched(true);
  }, [isEditing, detailQ.data]);

  useEffect(() => {
    if (!isEditing || !editVersionQ.data) return;
    setInitialVersionLabel(editVersionQ.data.version_label ?? `v${editVersionQ.data.version_number}`);
    setInstructions(editVersionQ.data.instructions ?? "");
  }, [isEditing, editVersionQ.data]);

  const saving = createMut.isPending || updateMut.isPending;

  const title = useMemo(
    () => (isEditing ? "Editar questionário" : "Novo questionário"),
    [isEditing],
  );

  function handleNameChange(value: string) {
    setName(value);
    if (!codeTouched) {
      setCode(normalizeQuestionnaireCode(value));
    }
  }

  function handleCodeChange(value: string) {
    setCodeTouched(true);
    setCode(normalizeQuestionnaireCode(value));
  }

  async function handleSubmit() {
    if (!code.trim()) {
      toast.error("Informe o código do questionário.");
      return;
    }
    if (!name.trim()) {
      toast.error("Informe o nome do questionário.");
      return;
    }

    try {
      if (isEditing && id) {
        const body: QuestionnaireUpdateIn = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || null,
          is_active: isActive,
          initial_version_label: initialVersionLabel.trim() || null,
          instructions: instructions.trim() || null,
        };
        await updateMut.mutateAsync({ id, body, scope });
        toast.success("Questionário atualizado com sucesso.");
        navigate(`${basePath}/${id}`);
        return;
      }

      const body: QuestionnaireCreateIn = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        initial_version_label: initialVersionLabel.trim() || "v1",
        instructions: instructions.trim() || null,
        scoring_method: "likert",
      };

      const created = await createMut.mutateAsync({ body, scope });
      toast.success("Questionário criado com sucesso.");
      navigate(`${basePath}/${created.id}`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar questionário.");
    }
  }

  return (
    <div>
      <PageHeader
        title={title}
        description="Defina o cabeçalho do questionário e, na criação, a configuração inicial da primeira versão."
      >
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                isEditing && id ? `${basePath}/${id}` : basePath,
              )
            }
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Dados do questionário</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-10">
            <div className="md:col-span-4">
              <Label>Nome *</Label>
              <Input
                className="mt-1"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ex.: Avaliação Psicossocial NR1"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Código *</Label>
              <Input
                className="mt-1"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="Gerado automaticamente a partir do nome"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Versão</Label>
              <Input
                className="mt-1"
                value={initialVersionLabel}
                onChange={(e) => setInitialVersionLabel(e.target.value)}
                placeholder="v1"
              />
            </div>


          </div>

          <div>
            <Label>Descrição</Label>
            <textarea
              className="mt-1 min-h-[120px] w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição opcional"
            />
          </div>

          <div>
            <Label>Instruções iniciais</Label>
            <textarea
              className="mt-1 min-h-[120px] w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Instruções opcionais para a versão inicial"
            />
          </div>

          <div className="max-w-xs">
            <Label>Status</Label>
            <Select
              value={isActive ? "active" : "inactive"}
              onValueChange={(value) => setIsActive(value === "active")}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

        </CardContent>
      </Card>
    </div>
  );
}
